import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, attempts: 0, error: null });
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null, attempts: 0, error: "Missing session id" });
      return;
    }
    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid" || data.status === "completed") {
          setState({ phase: "paid", data, attempts: attempt, error: null });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ phase: "failed", data, attempts: attempt, error: data.payment_status });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data, attempts: attempt, error: null });
          return;
        }
        setState({ phase: "polling", data, attempts: attempt, error: null });
        timer.current = setTimeout(() => poll(attempt + 1), INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "error", data: null, attempts: attempt, error: e?.response?.data?.detail || e.message });
      }
    };

    poll(1);
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto text-center py-6" data-testid="payment-success-page">
      {state.phase === "polling" && (
        <div className="card-editorial p-10 flex flex-col items-center gap-4" data-testid="payment-polling">
          <Loader2 className="animate-spin text-terracotta" size={40} />
          <h1 className="font-serif text-3xl text-stone-900">Confirming your payment…</h1>
          <p className="text-sm text-stone-500">Check {state.attempts} of {MAX_ATTEMPTS}</p>
          <p className="data-mono text-xs text-stone-400 mt-1">session: {sessionId?.slice(0, 24)}…</p>
        </div>
      )}
      {state.phase === "paid" && (
        <div className="card-editorial p-10 flex flex-col items-center gap-4" data-testid="payment-paid">
          <CheckCircle2 className="text-terracotta" size={48} />
          <h1 className="font-serif text-4xl text-stone-900">Welcome to Premium.</h1>
          <p className="text-stone-600 max-w-sm">
            The ads are gone. Your rhythm is yours alone now.
          </p>
          <Link to="/" className="btn-accent mt-2" data-testid="post-payment-home">
            Back to Today <ChevronRight size={16} />
          </Link>
        </div>
      )}
      {state.phase === "failed" && (
        <div className="card-editorial p-10 flex flex-col items-center gap-4" data-testid="payment-failed">
          <XCircle className="text-stone-500" size={48} />
          <h1 className="font-serif text-3xl text-stone-900">Payment didn&apos;t complete.</h1>
          <p className="text-stone-600">You can try again anytime.</p>
          <Link to="/premium" className="btn-outline mt-2">Try again</Link>
        </div>
      )}
      {state.phase === "timeout" && (
        <div className="card-editorial p-10 flex flex-col items-center gap-4" data-testid="payment-timeout">
          <Loader2 className="text-stone-500" size={40} />
          <h1 className="font-serif text-3xl text-stone-900">Still confirming…</h1>
          <p className="text-stone-600 text-sm">Stripe is taking longer than usual. Refresh in a moment.</p>
          <button onClick={() => window.location.reload()} className="btn-outline" data-testid="payment-retry">
            Check again
          </button>
        </div>
      )}
      {state.phase === "error" && (
        <div className="card-editorial p-10 flex flex-col items-center gap-4" data-testid="payment-error">
          <XCircle className="text-stone-500" size={48} />
          <h1 className="font-serif text-3xl text-stone-900">Something went sideways.</h1>
          <p className="text-sm text-stone-500">{state.error}</p>
          <Link to="/premium" className="btn-outline">Back to upgrade</Link>
        </div>
      )}
    </div>
  );
}
